import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Layers, AlertTriangle, Package } from "lucide-react";
import { formatMeters, statusColor } from "@/lib/format";

export default function Inventory() {
  const [data, setData] = useState({ fabrics: [], low_stock_threshold: 10, critical_threshold: 5 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory/overview");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const max = Math.max(1, ...data.fabrics.map((f) => f.current_stock || 0));

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="inventory-page">
      <Header subtitle="Stock overview" />
      <main className="max-w-md mx-auto px-5 pt-5 pb-32 space-y-5">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-900 tracking-tight">
            Inventory
          </h1>
          <p className="text-brand-700/70 mt-1 text-sm">
            Thresholds: low ≤ {formatMeters(data.low_stock_threshold)}m, critical ≤ {formatMeters(data.critical_threshold)}m.
          </p>
        </div>

        {loading ? (
          <div className="bg-white rounded-3xl border border-brand-200 p-6 text-sm text-brand-700/70">
            Loading inventory…
          </div>
        ) : data.fabrics.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-200 p-10 text-center" data-testid="inventory-empty">
            <div className="w-14 h-14 rounded-2xl bg-brand-100 mx-auto flex items-center justify-center mb-3">
              <Layers className="w-6 h-6 text-brand-700/60" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold text-brand-900">No fabrics yet</h3>
            <p className="text-sm text-brand-700/70 mt-1">Ask admin to add fabrics in the Admin panel.</p>
          </div>
        ) : (
          <>
            {/* Bar chart */}
            <section
              className="bg-white rounded-3xl border border-brand-200 p-5"
              data-testid="fabric-bar-chart"
            >
              <h2 className="font-heading text-lg font-semibold text-brand-900 mb-4">
                Stock levels
              </h2>
              <ul className="space-y-3">
                {data.fabrics.map((f) => {
                  const tone = statusColor(f.status);
                  const w = Math.max(2, Math.round((f.current_stock / max) * 100));
                  return (
                    <li
                      key={f.id}
                      className="flex items-center gap-3"
                      data-testid={`chart-row-${f.id}`}
                    >
                      <div className="w-28 shrink-0 text-sm font-medium text-brand-900 truncate">
                        {f.name}
                      </div>
                      <div className="flex-1 h-3 rounded-full bg-brand-100 overflow-hidden">
                        <div
                          className={`h-full ${tone.bar} rounded-full transition-all duration-500`}
                          style={{ width: `${w}%` }}
                        />
                      </div>
                      <div className="w-16 text-right font-heading text-sm font-semibold text-brand-900 tabular-nums">
                        {formatMeters(f.current_stock)}m
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* Detail cards */}
            <section className="space-y-3" data-testid="inventory-cards">
              {data.fabrics.map((f) => {
                const tone = statusColor(f.status);
                return (
                  <article
                    key={f.id}
                    className="bg-white rounded-3xl border border-brand-200 p-5"
                    data-testid={`fabric-card-${f.id}`}
                  >
                    <div className="flex items-start gap-4 mb-3">
                      <div
                        className="w-20 h-20 rounded-2xl bg-brand-100 overflow-hidden shrink-0 flex items-center justify-center"
                        data-testid={`fabric-image-${f.id}`}
                      >
                        {f.image_path ? (
                          <img src={fileUrl(f.image_path)} alt={f.name} className="w-full h-full object-cover" />
                        ) : (
                          <Layers className="w-6 h-6 text-brand-700/40" strokeWidth={1.4} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-heading text-xl font-semibold text-brand-900 truncate">
                            {f.name}
                          </h3>
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${tone.bg} ${tone.text} shrink-0`}
                            data-testid={`fabric-status-${f.id}`}
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${tone.dot} mr-1.5`} />
                            {f.status}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className={`font-heading text-3xl font-bold tracking-tight ${tone.text}`}>
                            {formatMeters(f.current_stock)}
                          </span>
                          <span className="text-xs text-brand-700/60 font-medium">m in stock</span>
                        </div>
                      </div>
                    </div>

                    {f.status === "critical" && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-2xl">
                        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                        <span>Reorder needed</span>
                      </div>
                    )}

                    {f.linked_products.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-brand-100">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-brand-700/60 font-medium mb-2">
                          Units remaining per product
                        </p>
                        <ul className="space-y-1.5">
                          {f.linked_products.map((p) => (
                            <li
                              key={p.product_id}
                              className="flex items-center justify-between text-sm"
                              data-testid={`units-${f.id}-${p.product_id}`}
                            >
                              <span className="text-brand-900 truncate">
                                <Package className="inline w-3 h-3 mr-1 text-brand-700/60" strokeWidth={1.8} />
                                {p.product_name}
                              </span>
                              <span className="text-brand-700/80 tabular-nums shrink-0 ml-2">
                                <strong>{p.units_remaining}</strong> unit{p.units_remaining !== 1 ? "s" : ""}
                                <span className="text-brand-700/50 ml-1">
                                  ({formatMeters(p.meters_per_unit)}m each)
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {f.linked_products.length === 0 && (
                      <p className="text-xs text-brand-700/50 mt-3">
                        No products linked yet.
                      </p>
                    )}
                  </article>
                );
              })}
            </section>
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
